import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  createWorkbook, 
  updateWorkbook,
  deleteWorkbook
} from '../workbook-operations';
import { Project } from '../workbook-types';
import { useToast } from '@/hooks/errors/use-toast';
import { pageKeys } from './workbook-query-keys';

/**
 * Hook for workbook CRUD mutations (create, update, delete)
 * Uses unified workbook-projects query keys for consistency
 */
export function usePageMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Simplified: Use only the workbook-projects query system
  const invalidateWorkbookQueries = () => {
    // CRITICAL FIX: Invalidate ALL workbook-projects queries regardless of filter parameters
    // This ensures Add to Project dropdowns update immediately when new projects are created
    queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
    
    // Also invalidate unified counts query for sidebar updates
    queryClient.invalidateQueries({ queryKey: ['workbooks-with-tags-unified'], exact: false });
    
    // Legacy count queries (kept for backward compatibility)
    queryClient.invalidateQueries({ queryKey: ['workbook-count-all'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-pinned'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-archived'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-total-user'], exact: false });
  };
  
  const createPageMutation = useMutation({
    mutationFn: (workbook: Partial<Project>) => createWorkbook(workbook),
    onSuccess: () => {
      invalidateWorkbookQueries();
      toast({
        title: "Success",
        description: "Workbook created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating workbook:', error);
      toast({
        title: "Error",
        description: "Failed to create workbook",
        variant: "destructive",
      });
    },
  });
  
  const updatePageMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<Project> }) => updateWorkbook(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['workbook-projects'] });

      // Snapshot the previous value for rollback
      const previousWorkbooks = queryClient.getQueryData(['workbook-projects']);

      // Optimistically update the cache
      queryClient.setQueriesData({ queryKey: ['workbook-projects'] }, (old: any) => {
        if (!old?.projects) return old;
        
        return {
          ...old,
          projects: old.projects.map((project: Project) =>
            project.id === id ? { ...project, ...updates } : project
          )
        };
      });

      // Also update individual workbook query if it exists
      queryClient.setQueryData(pageKeys.detail(id), (old: any) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      // Also optimistically update project navigation query
      queryClient.setQueryData(['project-navigation', id], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          project: { ...old.project, ...updates }
        };
      });

      return { previousWorkbooks };
    },
    onError: (err, { id, updates }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousWorkbooks) {
        queryClient.setQueryData(['workbook-projects'], context.previousWorkbooks);
      }
      // Also invalidate project navigation to restore accurate state
      queryClient.invalidateQueries({ queryKey: ['project-navigation', id] });
      console.error('Error updating workbook:', err);
      toast({
        title: "Error",
        description: "Failed to update workbook",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      // But use a more targeted approach - only invalidate if needed
      // This can be commented out for pure optimistic updates
      // queryClient.invalidateQueries({ queryKey: ['workbook-projects'] });
    },
  });
  
  const deletePageMutation = useMutation({
    mutationFn: deleteWorkbook,
    onSuccess: () => {
      invalidateWorkbookQueries();
      toast({
        title: "Success",
        description: "Workbook deleted permanently",
      });
    },
    onError: (error) => {
      console.error('Error deleting workbook:', error);
      toast({
        title: "Error", 
        description: "Failed to delete workbook",
        variant: "destructive",
      });
    },
  });
  
  return {
    createPage: createPageMutation.mutate,
    updatePage: updatePageMutation.mutate,
    updatePageAsync: updatePageMutation.mutateAsync,
    deletePage: deletePageMutation.mutate,
    isPending: 
      createPageMutation.isPending || 
      updatePageMutation.isPending || 
      deletePageMutation.isPending
  };
}
